/**
 * WaitlistPopup - Sticky iOS waitlist banner
 *
 * Fixed banner at bottom of screen prompting users to join iOS waitlist.
 * Shows application status after joining.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export default function WaitlistPopup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isDismissed, setIsDismissed] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState('pending');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkWaitlistStatus();
  }, [user]);

  const checkWaitlistStatus = async () => {
    setLoading(true);
    try {
      // Check localStorage first
      const joinedFromStorage = localStorage.getItem('waitlist_joined');

      if (user) {
        // Check Firestore for logged-in user
        try {
          const waitlistRef = collection(db, 'waitlist');
          const q = query(waitlistRef, where('userId', '==', user.uid));
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            const entry = snapshot.docs[0].data();
            setHasJoined(true);
            setWaitlistStatus(entry.status || 'pending');
          } else if (joinedFromStorage) {
            setHasJoined(true);
          }
        } catch (firestoreError) {
          // If Firestore fails, fall back to localStorage
          console.log('Using localStorage for waitlist status');
          if (joinedFromStorage) {
            setHasJoined(true);
          }
        }
      } else if (joinedFromStorage) {
        // For non-logged in users, just use localStorage (can't query Firestore)
        setHasJoined(true);
        // Status stays as 'pending' - they need to log in to see real status
      }
    } catch (error) {
      console.error('Error checking waitlist status:', error);
    } finally {
      setLoading(false);
      setIsDismissed(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved':
        return {
          label: 'Approved',
          color: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          message: 'You have access! Download the app.'
        };
      case 'rejected':
        return {
          label: 'Not Selected',
          color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          message: 'Not selected for early access.'
        };
      default:
        return {
          label: 'Pending',
          color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          message: 'Application under review.'
        };
    }
  };

  if (isDismissed || loading) return null;

  const statusConfig = getStatusConfig(waitlistStatus);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 animate-slide-up">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gradient-to-r dark:from-[#1c1c1e] dark:to-[#2c2c2e] rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl shadow-black/20 dark:shadow-black/50 p-4 flex items-center gap-4">
          {/* iOS Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-gray-900 dark:text-white font-semibold text-sm sm:text-base">QuickMeet for iOS</p>
              {hasJoined ? (
                <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium flex items-center gap-1 border ${statusConfig.color}`}>
                  {statusConfig.icon}
                  {statusConfig.label}
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-[#06c]/10 dark:bg-[#06c]/20 text-[#06c] text-[10px] sm:text-xs font-medium">
                  Coming Soon
                </span>
              )}
            </div>
            <p className="text-gray-500 dark:text-white/50 text-xs sm:text-sm">
              {hasJoined ? statusConfig.message : 'Get early access to our native iOS app'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasJoined ? (
              waitlistStatus === 'approved' ? (
                <a
                  href="https://apps.apple.com/app/quickmeet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl bg-green-500 text-white text-xs sm:text-sm font-medium hover:bg-green-600 transition-colors"
                >
                  Download
                </a>
              ) : (
                <button
                  onClick={() => navigate('/waitlist')}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl bg-[#f5f5f7] dark:bg-white/10 text-gray-700 dark:text-white text-xs sm:text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
                >
                  View Details
                </button>
              )
            ) : (
              <button
                onClick={() => navigate('/waitlist')}
                className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl bg-[#06c] text-white text-xs sm:text-sm font-medium hover:bg-[#0055d4] transition-colors"
              >
                Join Waitlist
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4 text-gray-400 dark:text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
